import React, { useEffect, useState } from "react";

import {
  IconButton,
  ImageList,
  ImageListItem,
  makeStyles,
} from "@material-ui/core";
import AddPhotoAlternateRoundedIcon from "@material-ui/icons/AddPhotoAlternateRounded";
import CheckRoundedIcon from "@material-ui/icons/CheckRounded";
import DeleteIcon from "@material-ui/icons/Delete";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import { createStyles, Theme } from "@material-ui/core/styles";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";

import ConfirmationModal from "../../../../components/ConfirmationModal";
import "./style.css";
import {
  addHospitalPicture, addHospitalPictureById,
  approvePicture,
  deleteHospitalPicture, deleteHospitalPictureById,
  deletePicture,
  getHospitalPictures, getHospitalPicturesById,
} from "../../../../api/hospital";
import { Image } from "./types";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: "0px !important",
      width: "auto !important",
    },
    imageList: {
      flexWrap: "nowrap",
      [theme.breakpoints.only("xs")]: {
        flexDirection: "column",
        marginBottom: 20,
      },
    },
    input: { display: "none" },
    upload: {
      width: "13rem",
      height: "91%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: `4px solid ${theme.palette.secondary.dark}`,
      margin: 4,
      "&:hover": {
        cursor: "pointer",
      },
      [theme.breakpoints.only("xs")]: {
        width: "auto",
      },
    },
    addPhoto: {
      fontSize: 40,
      color: theme.palette.primary.main,
    },
    mobile: {
      [theme.breakpoints.only("xs")]: {
        width: "100% !important",
      },
    },
    purpleCircle: {
      background: theme.palette.secondary.main,
      borderRadius: "50%",
      padding: theme.spacing(1),
      color: "#fff",
      margin: theme.spacing(0, 1, 0, 5),
      [theme.breakpoints.only("xs")]: {
        display: "inline-flex",
        margin: theme.spacing(1, 1),
      },
    },
  })
);

type NurseGalleryProps = {
  galleryProp?: Image[];
  isReview?: boolean;
  handleGalleryChange?: (gallery: Image[]) => void;
  hospitalId?: number;
};

export default function NurseGallery({
  galleryProp,
  isReview,
  handleGalleryChange,
  hospitalId,
}: NurseGalleryProps) {
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number>();
  const [failedUpload, setFailedUpload] = useState<boolean>(false);
  const [imageIdToDelete, setImageIdToDelete] = useState<number>();
  const [imageIdToApprove, setImageIdToApprove] = useState<number>();
  const [gallery, setGallery] = useState<Image[]>([]);

  const toggleOpen = () => {
    setModalOpen((modalOpen) => !modalOpen);
    if (imageIdToDelete !== undefined) {
      setImageIdToDelete(undefined);
    }
    if (imageIdToApprove !== undefined) {
      setImageIdToApprove(undefined);
    }
    if (failedUpload) {
      setFailedUpload(false);
    }
  };

  async function handleImageDelete(imageID: number) {
    if (isReview) {
      await deletePicture(imageID);
    } else if (hospitalId) {
      await deleteHospitalPictureById(hospitalId, imageID);
    } else {
      await deleteHospitalPicture(imageID);
    }
    setGallery([...gallery.filter((image) => image.id !== imageID)]);
    if (handleGalleryChange) {
      handleGalleryChange([...gallery.filter((image) => image.id !== imageID)]);
    }
    toggleOpen();
  }

  async function handleImageApprove(imageID: number) {
    await approvePicture(imageID);
    setGallery([...gallery.filter((image) => image.id !== imageID)]);
    if (handleGalleryChange) {
      handleGalleryChange([...gallery.filter((image) => image.id !== imageID)]);
    }
    toggleOpen();
  }

  useEffect(() => {
    async function fetchHospitalPictures() {
      const response = hospitalId
        ? await getHospitalPicturesById(hospitalId)
        : await getHospitalPictures();
      setGallery(response.data.gallery);
    }

    if (!galleryProp) {
      fetchHospitalPictures();
    } else {
      setGallery(galleryProp);
    }
  }, [galleryProp, hospitalId]);

  const convertToBase64 = (file: File) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = () => {
        handlePictureUploadException();
        return;
      };
    });
  };

  const handlePictureUploadException = () => {
    setFailedUpload(true);
    setModalOpen((modalOpen) => !modalOpen);
    return;
  };

  const handlePictureUpload = async (e: any) => {
    const imageFile = e.target.files[0];
    if (imageFile === undefined) {
      return;
    }
    if (
      !imageFile.type.startsWith("image") ||
      imageFile.size / (1024 * 1024) > 3 ||
      gallery.length >= 25
    ) {
      handlePictureUploadException();
      e.target.value = "";
      return;
    }

    const base64 = (await convertToBase64(imageFile)) as string;
    const imageToSave = { pictureBase64: base64 };

    const picture = hospitalId
      ? await addHospitalPictureById(hospitalId, imageToSave)
      : await addHospitalPicture(imageToSave);
    e.target.value = "";
    setGallery([
      {
        id: picture?.data.id,
        pictureBase64: picture?.data.pictureBase64,
      },
      ...gallery,
    ]);
  };

  return (
    <ImageList className={classes.imageList} cols={4} gap={8}>
      {!isReview && (
        <div className={classes.root}>
          <input
            accept="image/*"
            className={classes.input}
            id="icon-button-photo"
            type="file"
            onChange={handlePictureUpload}
          />
          <label htmlFor="icon-button-photo">
            <div className={classes.upload}>
              <IconButton color="primary" component="span">
                <AddPhotoAlternateRoundedIcon className={classes.addPhoto} />
              </IconButton>
            </div>
          </label>
        </div>
      )}
      {gallery.map((image) => (
        <ImageListItem key={image.id} className={`${classes.mobile} image`}>
          <img src={image.pictureBase64} alt="hospital" />
          <div className="overlay" />
          {/* TODO think about new DeleteAction subcomponent which handles everything about deletion */}
          {isReview && (
            <CheckRoundedIcon
              className="approve"
              onClick={() => {
                setImageIdToApprove(image.id);
                setModalOpen(true);
              }}
            />
          )}
          <DeleteIcon
            className="delete"
            onClick={() => {
              setImageIdToDelete(image.id);
              setModalOpen((modalOpen) => !modalOpen);
            }}
          />
          <ZoomInIcon
            className="delete zoom"
            onClick={() => {
              setCurrentIndex(gallery.indexOf(image));
            }}
          />
        </ImageListItem>
      ))}
      {imageIdToDelete !== undefined && (
        <ConfirmationModal
          modalOpen={modalOpen}
          handleClose={toggleOpen}
          handleAction={() => handleImageDelete(imageIdToDelete)}
          modalText={'Klikom na gumb "U redu" fotografija će biti izbrisana.'}
          title={"Jeste li sigurni da želite izbrisati fotografiju?"}
        />
      )}
      {imageIdToApprove !== undefined && (
        <ConfirmationModal
          modalOpen={modalOpen}
          handleClose={toggleOpen}
          handleAction={() => handleImageApprove(imageIdToApprove)}
          modalText={'Klikom na gumb "U redu" fotografija će biti odobrena.'}
          title={"Jeste li sigurni da želite odobriti fotografiju?"}
        />
      )}
      {failedUpload && (
        <ConfirmationModal
          modalOpen={modalOpen}
          handleClose={toggleOpen}
          modalText={"Neuspješan prijenos fotografije"}
          title={"Pogreška!"}
        />
      )}
      {currentIndex !== undefined && (
        <Lightbox
          mainSrc={gallery[currentIndex].pictureBase64}
          nextSrc={gallery[(currentIndex + 1) % gallery.length].pictureBase64}
          prevSrc={
            gallery[(currentIndex + gallery.length - 1) % gallery.length]
              .pictureBase64
          }
          onCloseRequest={() => {
            setCurrentIndex(undefined);
          }}
          onMovePrevRequest={() =>
            setCurrentIndex(
              (currentIndex + gallery.length - 1) % gallery.length
            )
          }
          onMoveNextRequest={() =>
            setCurrentIndex((currentIndex + 1) % gallery.length)
          }
        />
      )}
    </ImageList>
  );
}
